import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import type {
  AppliedState,
  BackupFile,
  FallbackRule,
  LogEvent,
  MeResponse,
  Preset,
  Rule,
  RulePredicate,
  ActionKind,
  Effect,
  StatusResponse,
  WorkingHoursEntryInput,
  WorkingHoursRow,
} from "./types";

// ---- Auth ----

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: () => api<MeResponse>("/auth/me"),
    staleTime: 30_000,
  });
}

export function useLogin() {
  return useMutation({
    mutationFn: (body: { username: string; password: string; remember: boolean }) =>
      api<{ username: string; csrf_token: string; remember: boolean; expires_ts: number }>(
        "/auth/login",
        { method: "POST", body },
      ),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>("/auth/logout", { method: "POST" }),
    onSuccess: () => qc.clear(),
  });
}

// ---- Status / state ----

export function useState_() {
  return useQuery({
    queryKey: ["state"],
    queryFn: () => api<AppliedState>("/api/state"),
    refetchInterval: 5_000,
  });
}

export function useStatus() {
  return useQuery({
    queryKey: ["status"],
    queryFn: () => api<StatusResponse>("/api/status"),
    refetchInterval: 5_000,
  });
}

// ---- Event log ----

export function useLogs(limit = 10) {
  return useQuery({
    queryKey: ["logs", limit],
    queryFn: () => api<{ events: LogEvent[] }>(`/api/logs?limit=${limit}`),
    refetchInterval: 5_000,
  });
}

export function useClearLogs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>("/api/logs", { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["logs"] }),
  });
}

// ---- Pause ----

export function usePause() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (duration_seconds: number | null) =>
      api<{ ok: true; expires_ts: number | null }>("/api/pause", {
        method: "POST",
        body: { duration_seconds },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["state"] });
      qc.invalidateQueries({ queryKey: ["status"] });
    },
  });
}

export function useClearPause() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>("/api/pause", { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["state"] });
      qc.invalidateQueries({ queryKey: ["status"] });
    },
  });
}

// ---- Override ----

export function useOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { color: string; luminance: number; effect: Effect; ttl_seconds: number | null }) =>
      api<{ ok: true; expires_ts: number | null }>("/api/override", {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["state"] });
      qc.invalidateQueries({ queryKey: ["status"] });
    },
  });
}

export function useClearOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>("/api/override", { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["state"] });
      qc.invalidateQueries({ queryKey: ["status"] });
    },
  });
}

// ---- Rules ----

export function useRules() {
  return useQuery({
    queryKey: ["rules"],
    queryFn: () => api<{ rules: Rule[]; version: number }>("/api/rules"),
  });
}

export function useCreateRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      predicate: RulePredicate;
      action_kind: ActionKind;
      preset_id?: number | null;
    }) => api<{ rule: Rule }>("/api/rules", { method: "POST", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rules"] }),
  });
}

export function useUpdateRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: number;
      name?: string;
      predicate?: RulePredicate;
      action_kind?: ActionKind;
      preset_id?: number | null;
      clear_preset?: boolean;
      enabled?: boolean;
    }) => api<{ rule: Rule }>(`/api/rules/${id}`, { method: "PATCH", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rules"] }),
  });
}

export function useDeleteRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api<void>(`/api/rules/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rules"] }),
  });
}

export function useFallbackRule() {
  return useQuery({
    queryKey: ["fallback_rule"],
    queryFn: () => api<{ fallback: FallbackRule }>("/api/rules/fallback"),
  });
}

export function useUpdateFallbackRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      enabled?: boolean;
      action_kind?: "preset" | "do_nothing";
      preset_id?: number | null;
      clear_preset?: boolean;
    }) =>
      api<{ fallback: FallbackRule }>("/api/rules/fallback", {
        method: "PATCH",
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fallback_rule"] });
      qc.invalidateQueries({ queryKey: ["rules"] });
    },
  });
}

export function useReorderRules() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (order: number[]) =>
      api<{ rules: Rule[] }>("/api/rules/reorder", {
        method: "POST",
        body: { order },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rules"] }),
  });
}

// ---- Presets ----

export function usePresets() {
  return useQuery({
    queryKey: ["presets"],
    queryFn: () => api<{ presets: Preset[] }>("/api/presets"),
  });
}

export function useCreatePreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; color: string; luminance: number; effect: Effect }) =>
      api<{ preset: Preset }>("/api/presets", { method: "POST", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["presets"] });
      qc.invalidateQueries({ queryKey: ["rules"] });
    },
  });
}

export function useUpdatePreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: number;
      name?: string;
      color?: string;
      luminance?: number;
      effect?: Effect;
    }) => api<{ preset: Preset }>(`/api/presets/${id}`, { method: "PATCH", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["presets"] });
      qc.invalidateQueries({ queryKey: ["rules"] });
    },
  });
}

export function useDeletePreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api<void>(`/api/presets/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["presets"] });
      qc.invalidateQueries({ queryKey: ["rules"] });
    },
  });
}

// ---- Working hours ----

export function useWorkingHours() {
  return useQuery({
    queryKey: ["working_hours"],
    queryFn: () => api<{ entries: WorkingHoursRow[] }>("/api/working-hours"),
  });
}

export function useReplaceWorkingHours() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entries: WorkingHoursEntryInput[]) =>
      api<{ entries: WorkingHoursRow[] }>("/api/working-hours", {
        method: "PUT",
        body: { entries },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["working_hours"] }),
  });
}

// ---- Config backup ----

export async function downloadConfigBackup(): Promise<void> {
  const resp = await fetch("/api/config/export", {
    method: "GET",
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  if (!resp.ok) {
    throw new Error(`export failed: ${resp.status}`);
  }
  const disposition = resp.headers.get("content-disposition") ?? "";
  const match = /filename=([^;]+)/i.exec(disposition);
  const filename = (match?.[1] ?? "led-controller-backup.json").trim().replace(/^"|"$/g, "");
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function useImportConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: BackupFile) =>
      api<{ ok: true; counts: { presets: number; rules: number; working_hours: number } }>(
        "/api/config/import",
        { method: "POST", body: file },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rules"] });
      qc.invalidateQueries({ queryKey: ["presets"] });
      qc.invalidateQueries({ queryKey: ["working_hours"] });
    },
  });
}

